import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, of, switchMap, takeWhile, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';

import { StoreApiService } from '@paymash/local-sdk/api/v2';

import { CompanyStoresActions, CompanyStoresApiActions } from './company-stores.actions';
import { selectCompanyUuid } from '@public-page/store/company';
import { selectQueryParam } from '@public-page/store/router';
import { Router } from '@angular/router';
import { GlobalService } from '@modules/core/services';

@Injectable()
export class CompanyStoreEffects {
  constructor(
    private actions$: Actions,
    private storeApiService: StoreApiService,
    private store: Store,
    private router: Router,
    private globalService: GlobalService
  ) {}

  loadStores$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CompanyStoresActions.load),
      switchMap(() =>
        this.store.select(selectCompanyUuid).pipe(
          exhaustMap((companyUuid) => this.storeApiService.getStores(companyUuid as string)),
          map((stores) => CompanyStoresApiActions.loadSuccess({ stores })),
          catchError(() => of(CompanyStoresApiActions.loadFailure))
        )
      )
    );
  });

  setCurrentStore$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      switchMap(() => this.store.select(selectQueryParam('storeUuid'))),
      takeWhile((value) => value !== undefined),
      map((uuid) => CompanyStoresActions.select({ storeUuid: uuid as string }))
    );
  });

  redirectToSelectServices$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CompanyStoresActions.redirectAndSelectStore),
      concatLatestFrom(() => [this.store.select(selectCompanyUuid)]),
      tap(([{ storeUuid }, companyUuid]) => {
        const path = this.globalService.hasCustomDomain
          ? ['booking', 'select-service']
          : [companyUuid, 'booking', 'select-service'];

        this.router.navigate(path, {
          queryParams: { storeUuid },
        });
      }),
      map(([{ storeUuid }]) => {
        return CompanyStoresActions.select({ storeUuid });
      })
    );
  });

  redirectToSelfOrdering = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CompanyStoresActions.redirectToSelfOrdering),
        concatLatestFrom(() => [this.store.select(selectCompanyUuid)]),
        tap(([{ storeUuid }, companyUuid]) => {
          this.globalService.redirectToSelfOrdering(companyUuid, storeUuid);
        })
      );
    },
    { dispatch: false }
  );
}
